import { Card, CardContent, Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import React, { useEffect, useState } from "react";
import CcaAcademicYearInput from "./CcaAcademicYearInput";
import {
  RadioButtonGroupInput,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  BooleanInput,
} from "react-admin";
import { CcaAcademicYearResources, SchoolIdSelected } from "utils/tools";
import CcaAddressAutoCompleteInput from "components/CcaAddressAutoCompleteInput";
import FixedLocationDaysOfWeekInput from "./CcaFixedLocationDaysOfWeekInput";
import CcaDaysOfWeekInput from "./CcaDaysOfWeekInput";
import moment from "moment/moment";
import StudentScheduleStartDateInput from "../../../components/StudentScheduleStartDateInput";
import {useField} from "react-final-form";

interface ICcaCostCalculationProp {
  dropOffTypeCallback: (value: string) => void;
}

interface ISelectOption {
  id: number;
  name: string;
}

const CcaCostCalculationInput = ({
  dropOffTypeCallback
}: ICcaCostCalculationProp) => {
  const schoolId = Number(SchoolIdSelected());
  const [school, setSchool] = useState<ISchool>();
  const [ccaDropOffType, setCcaDropOffType] = useState<string>();
  const [idCcaAcademicYear, setIdCcaAcademicYear] = useState<number>(0);
  const [fixLocationOptions, setFixLocationOptions] = useState<ISelectOption[]>(
    []
  );
  const [isUseOneFixedLocation, setIsUseOneFixedLocation] = useState<boolean>(true);

  const provider = useDataProvider();

  const academicYearField = useField("ccaAcademicYearId");
  const date = useField("ccaDate");

  useEffect(() => {
    academicYearField.input.onChange("");
    date.input.onChange("");
    ccaAcademicYearCallback(0);
  }, []);

  useEffect(() => {
    if (schoolId) {
      provider
        .getList("ccaFixedLocationDropOffs", {
          filter: { SchoolId: schoolId, Status: "active" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          const fixLocations = data as ICcaFixedLocationDropOff[];
          const options = fixLocations.map((location) => ({
            id: location.id,
            name: location.name,
          }));
          setFixLocationOptions(options);
        });

      provider.getOne("schools", { id: schoolId }).then(({ data }) => {
        setSchool(data as ISchool);

        if (data.ccaDropOffType !== "both") {
          onDropOffTypeChange(data.ccaDropOffType);
        } else {
          onDropOffTypeChange("");
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const ccaAcademicYearResources = CcaAcademicYearResources(idCcaAcademicYear);

  const onDropOffTypeChange = (value: string) => {
    setCcaDropOffType(value);
    dropOffTypeCallback(value);
  };

  const ccaAcademicYearCallback = (value: number) => {
    setIdCcaAcademicYear(value);
  };

  return (
    <Card variant="outlined" style={{ marginBottom: 20 }}>
      <CardContent>
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">CCA Schedule</Typography>
          </Grid>

          <Grid item xs={4}>
            <CcaAcademicYearInput parentCallback={ccaAcademicYearCallback} />
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={4}>
            <StudentScheduleStartDateInput
              source="ccaDate"
              label="Start Date *"
              parentStartDate={moment
                .utc(ccaAcademicYearResources?.startDate)
                .format("yyyy-MM-DD")}
              parentEndDate={moment
                .utc(ccaAcademicYearResources?.endDate)
                .format("yyyy-MM-DD")}
            />
          </Grid>
          <Grid item xs={8}></Grid>

          {school && school.ccaDropOffType === "both" && (
            <Grid item xs={12}>
              <RadioButtonGroupInput
                label="Drop-off Address Type"
                source="dropOffTypeOption"
                choices={[
                  {
                    id: "fixed_location",
                    name: "Fixed Location",
                  },
                  {
                    id: "door_to_door",
                    name: "Door-to-door Location",
                  },
                ]}
                validate={required()}
                onChange={(e) => {
                  onDropOffTypeChange(e as unknown as string);
                }}
              />
            </Grid>
          )}

          {ccaDropOffType === "fixed_location" && (
            <>
              <Grid item xs={4} style={{ margin: "10px 0 -20px" }}>
                <BooleanInput
                  source="isUseOneFixedLocation"
                  label="Use One Fixed Location for All Selected Days"
                  defaultValue={true}
                  onChange={(e: boolean) => {
                    setIsUseOneFixedLocation(e);
                  }}
                />
              </Grid>

              {isUseOneFixedLocation && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6">Drop off Location</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <SelectInput
                      source="fixedLocationId"
                      label="Select Fixed Location"
                      choices={fixLocationOptions}
                      validate={required()}
                    />
                  </Grid>
                  <Grid item xs={8}></Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="h6">Days</Typography>
              </Grid>
              <Grid item xs={4}>
                <FixedLocationDaysOfWeekInput
                  isUseOneFixedLocation={isUseOneFixedLocation}
                  allowSelectWave={false}
                  schoolId={school.id}
                />
              </Grid>
            </>
          )}

          {ccaDropOffType === "door_to_door" && (
            <>
              <CcaDaysOfWeekInput />

              <Grid item xs={12}>
                <Typography variant="h6">Pick up & Drop off Address</Typography>
              </Grid>

              <Grid item xs={4}>
                <CcaAddressAutoCompleteInput
                  label="Postal Code *"
                  source="ccaPostalCode"
                  referenceSource="locationData"
                />
              </Grid>
              <Grid item xs={8}></Grid>

              <Grid item xs={2}>
                <TextInput label="Unit No." source="ccaUnitNumber" />
              </Grid>
              <Grid item xs={2}>
                <TextInput label="Block No." source="ccaBlockNumber" />
              </Grid>
              <Grid item xs={8}></Grid>

              <Grid item xs={4}>
                <TextInput label="Building Name" source="ccaBuilding" />
              </Grid>
              <Grid item xs={8}></Grid>

              <Grid item xs={4}>
                <TextInput
                  label="Street"
                  source="ccaStreet"
                  validate={required()}
                />
              </Grid>
              <Grid item xs={8}></Grid>
            </>
          )}
        </SanitizedGrid>
      </CardContent>
    </Card>
  );
};

export default CcaCostCalculationInput;
